import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "react-hot-toast";
import { API_BASE } from "../../config";
import { User, Mail, Lock, Eye, EyeOff, Clock } from "lucide-react";

const RENEW_AMOUNT = 3999; // 💳 Renewal cost

const Profile = () => {
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const token = storedUser?.token;

  const [user, setUser] = useState(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [renewLoading, setRenewLoading] = useState(false);

  const strongPasswordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  // ✅ Fetch profile data
  const fetchProfile = async () => {
    if (!token) return navigate("/login");
    try {
      const res = await axios.get(`${API_BASE}/api/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const profile = res.data;
      setUser(profile);
      setUsername(profile.username);
      setEmail(profile.email);

      if (storedUser?.email && storedUser.email !== profile.email) {
        toast.success("✅ Email verified! Please log in again.");
        localStorage.removeItem("user");
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (err) {
      console.error("Failed to load profile:", err);
      toast.error("Failed to load profile");
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  // ✅ Update username
  const handleUsernameUpdate = async () => {
    if (!username.trim()) return toast.error("Username cannot be empty");
    setLoading(true);
    try {
      const res = await axios.put(
        `${API_BASE}/api/user/profile/username`,
        { username },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Username updated successfully!");

      const updatedUser = {
        ...storedUser,
        username: res.data?.user?.username || username,
      };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      window.dispatchEvent(new Event("userUpdated"));

      await fetchProfile(); // ✅ Refresh profile after update
    } catch (err) {
      console.error("Update username error:", err);
      toast.error(err.response?.data?.message || "Error updating username");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Request email change
  const handleEmailChange = async () => {
    if (!newEmail.trim()) return toast.error("Please enter a valid email");
    if (cooldown > 0) return toast.error(`Wait ${cooldown}s before resending`);
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/user/profile/request-email-change`,
        { newEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Verification email sent!");
      setNewEmail("");
      setCooldown(30);

      // ✅ Refresh profile to show pendingEmail immediately
      await fetchProfile();

      const countdown = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error("Email change error:", err);
      toast.error(err.response?.data?.message || "Error updating email");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Change password
  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword)
      return toast.error("Please fill all password fields");
    if (newPassword !== confirmPassword)
      return toast.error("Passwords do not match");
    if (!strongPasswordRegex.test(newPassword)) {
      return toast.error(
        "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character."
      );
    }

    setLoading(true);
    try {
      const res = await axios.put(
        `${API_BASE}/api/user/profile/change-password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Password changed successfully!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        localStorage.removeItem("user");
        navigate("/login");
      }, 2000);
    } catch (err) {
      console.error("Change password error:", err);
      toast.error(err.response?.data?.message || "Error changing password");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center h-[60vh] text-gray-500">
        Loading profile...
      </div>
    );
  }

  // ✅ Calculate subscription & countdown
  const expiryDate = user.subscriptionExpiresAt
  ? new Date(user.subscriptionExpiresAt)
  : null;

const now = new Date();

const hasSubscription = !!expiryDate;

const isActive =
  user.hasPaid && hasSubscription && expiryDate > now;

const isExpired =
  hasSubscription && expiryDate < now;

const isFreeUser =
  !hasSubscription && !user.hasPaid;

const daysLeft =
  isActive
    ? Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24))
    : 0;
  const showCountdown = isActive && daysLeft <= 15 && daysLeft > 0;
  const statusLabel = isFreeUser
  ? "Free Plan"
  : isActive
  ? "Active"
  : "Expired";

  return (
    <motion.div
      className="max-w-2xl mx-auto bg-white dark:bg-gray-900 shadow-lg rounded-2xl p-8 mt-10"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <h2 className="text-3xl font-semibold text-center mb-8 text-gray-800 dark:text-white">
        My Account
      </h2>

      {/* Username */}
      <div className="mb-6">
        <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-medium mb-2">
          <User size={18} /> Username
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="border dark:border-gray-700 w-full p-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-white"
          />
          <button
            onClick={handleUsernameUpdate}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-1"
          >
            Change
          </button>
        </div>
      </div>

      {/* Email */}
      <div className="mb-6">
        <label className="flex items-center gap-2 text-gray-700 dark:text-gray-300 font-medium mb-2">
          <Mail size={18} /> Current Email
        </label>
        <input
          type="text"
          value={email}
          disabled
          className="border dark:border-gray-700 w-full p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-500"
        />
        <div className="flex gap-2 mt-3">
          <input
            type="email"
            placeholder="Enter new email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="border dark:border-gray-700 w-full p-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-white"
          />
          <button
            onClick={handleEmailChange}
            disabled={loading || cooldown > 0}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            {cooldown > 0 ? `Wait ${cooldown}s` : "Change"}
          </button>
        </div>

        {user.pendingEmail && (
          <div className="mt-2 flex items-center gap-2 bg-yellow-50 dark:bg-yellow-900 border border-yellow-300 dark:border-yellow-700 p-3 rounded-lg">
            <Mail size={16} className="text-yellow-600" />
            <span className="text-yellow-700 dark:text-yellow-300 text-sm">
              📬 Verification pending for: <strong>{user.pendingEmail}</strong>
            </span>
          </div>
        )}
      </div>

      {/* Account Info */}
      <div className="grid grid-cols-2 gap-4 mt-6 border-t dark:border-gray-700 pt-6">
        <div>
          <label className="text-gray-500 text-sm">Joined On</label>
          <p className="font-medium text-gray-800 dark:text-white">
            {new Date(user.joinedOn).toLocaleDateString()}
          </p>
        </div>

        <div>
          <label className="text-gray-500 text-sm">Renewal Date</label>
          <p className="font-medium text-gray-800 dark:text-white">
            {expiryDate
  ? expiryDate.toLocaleDateString()
  : "No active subscription"}
          </p>
          {showCountdown && (
            <div className="mt-2 text-red-600 font-semibold flex items-center gap-2">
              <Clock size={16} />
              ⏳ {daysLeft} days left
            </div>
          )}
        </div>

        <div>
          <label className="text-gray-500 text-sm">Status</label>
          <span
            className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
              isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
      </div>

      {/* Change Password */}
<div className="border-t dark:border-gray-700 pt-6 mt-6">
  <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white flex items-center gap-2">
    <Lock size={18} /> Change Password
  </h3>

  <div className="space-y-4 relative">
    {[
      { label: "Current Password", value: currentPassword, setter: setCurrentPassword },
      { label: "New Password", value: newPassword, setter: setNewPassword },
      { label: "Confirm New Password", value: confirmPassword, setter: setConfirmPassword },
    ].map(({ label, value, setter }, i) => (
      <div key={i} className="relative">
        <input
          type={showPasswords ? "text" : "password"}
          placeholder={label}
          value={value}
          onChange={(e) => setter(e.target.value)}
          className="border dark:border-gray-700 w-full p-2 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-white"
        />
        {i === 2 && (
          <button
            type="button"
            onClick={() => setShowPasswords(!showPasswords)}
            className="absolute right-3 top-2.5 text-gray-500 hover:text-gray-700"
          >
            {showPasswords ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
    ))}

    {/* ✅ Password Requirements Checklist */}
    {newPassword && (
      <div className="mt-2 text-sm space-y-1">
        <p className={`${newPassword.length >= 8 ? "text-green-600" : "text-red-500"}`}>
          • Minimum 8 characters
        </p>
        <p className={`${/[A-Z]/.test(newPassword) ? "text-green-600" : "text-red-500"}`}>
          • At least one uppercase letter
        </p>
        <p className={`${/[a-z]/.test(newPassword) ? "text-green-600" : "text-red-500"}`}>
          • At least one lowercase letter
        </p>
        <p className={`${/[0-9]/.test(newPassword) ? "text-green-600" : "text-red-500"}`}>
          • At least one number
        </p>
        <p className={`${/[@$!%*?&]/.test(newPassword) ? "text-green-600" : "text-red-500"}`}>
          • At least one special character (@$!%*?&)
        </p>
      </div>
    )}

    <button
      onClick={handlePasswordChange}
      disabled={loading}
      className="bg-blue-600 hover:bg-blue-700 text-white w-full py-2 rounded-lg font-medium mt-4 disabled:opacity-60 disabled:cursor-not-allowed"
    >
      Change Password
    </button>
  </div>
</div>
    </motion.div>
  );
};

export default Profile;
